import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FilterMetadata, SelectOptionValue } from "../../types";
import {
    faSquareMinus,
    faSquarePlus,
} from "@fortawesome/pro-regular-svg-icons";
import { ImageLoader } from "~/components/ImageLoader";
import * as Analytics from "../../analytics";
import { Badge, BadgeVariants } from "~/components/Badge";
import { RWA_COIN_ICON } from "~/consts";
import { cn } from "~/lib/utils";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";

// // // //

/**
 * SelectAllButton
 * Button for selecting all entries
 */
function SelectAllButton(props: {
    value: SelectOptionValue[];
    filterMetadata: FilterMetadata;
    onChange: (updatedValue: SelectOptionValue[]) => void;
}) {
    const { value, filterMetadata, onChange } = props;
    const { selectOptions = [] } = filterMetadata;

    const allOptions = selectOptions.map((each) => each.value);

    const deselect = value.length > allOptions.length / 2 ? true : false;

    return (
        <button
            className="bg-transparent text-link hover:text-link-hover font-normal transition-colors flex items-center w-full justify-start whitespace-nowrap mb-2 outline-hidden"
            onClick={() => {
                if (deselect) {
                    Analytics.trackInlineMultiSelectDeselectAll({
                        field: filterMetadata.field,
                    });
                    onChange([]);
                    return;
                }

                Analytics.trackInlineMultiSelectSelectAll({
                    field: filterMetadata.field,
                });
                onChange(allOptions);
            }}
        >
            <FontAwesomeIcon
                icon={deselect ? faSquareMinus : faSquarePlus}
                className="mr-2 h-4 w-4"
            />
            {deselect ? "Deselect All" : "Select All"}
        </button>
    );
}

/**
 * StringMultiSelect
 * Renders checkboxes for filtering Investments by Protocol
 */
export function StringMultiSelect(props: {
    value: SelectOptionValue[];
    filterMetadata: FilterMetadata;
    disabled?: boolean;
    onChange: (updatedValue: SelectOptionValue[]) => void;
}) {
    const { filterMetadata, onChange, disabled = false } = props;
    const { content, selectOptions = [] } = filterMetadata;
    const value = Array.isArray(props.value) ? props.value : [];
    const [open, setOpen] = useState(false);

    // Debounce changes for 800ms
    // NOTE - we stop debouncing this here because it was introducing a tricky state bug, might re-add later?
    // const [value, onChange] = useDebouncedState<string[]>({
    //     debounceTimeout: 800,
    //     value: initialValue,
    //     onChange: props.onChange,
    // });

    // TODO - add support for content.description tooltip?

    // Determine if we should render two columns of results instead of one
    // (Currently we just do this if there are more than 10 options)
    const twoColumnLayout = selectOptions.length > 10;
    const threeColumnLayout = selectOptions.length > 30;

    // Determine if we should show an icon for each option
    const showOptionIcons = selectOptions.some((opt) => opt.content.icon);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
                className={cn(
                    "relative w-full cursor-default rounded-md bg-card py-1.5 pl-3 pr-10 text-left text-slate-800 shadow-xs ring-1 ring-inset ring-slate-300 focus:outline-hidden focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-5",
                    {
                        "cursor-not-allowed pointer-events-none bg-slate-100 text-slate-500 ring-slate-300":
                            disabled,
                    },
                )}
            >
                {content.icon && (
                    <ImageLoader
                        alt={`${content.label} Icon`}
                        className="rounded-xs w-5 shadow-lg"
                        src={content.icon}
                    />
                )}

                {/* Render label for select */}
                <span className="whitespace-nowrap">{content.label}</span>

                {/* Render count with number of selected items */}
                {value.length > 0 && (
                    <>
                        <Badge
                            label={value.length.toLocaleString()}
                            size="sm"
                            rounded="sm"
                            variant={BadgeVariants.gray}
                            className="whitespace-nowrap ml-2.5"
                        />
                    </>
                )}

                <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <FontAwesomeIcon
                        className="h-4 w-4 text-slate-400"
                        aria-hidden="true"
                        icon={faChevronDown}
                    />
                </span>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-4 w-full z-100">
                <SelectAllButton
                    value={value}
                    onChange={onChange}
                    filterMetadata={filterMetadata}
                />

                <div
                    className={cn("grid gap-4", {
                        "grid-cols-2 w-md":
                            twoColumnLayout && !threeColumnLayout,
                        "grid-cols-3 w-2xl": threeColumnLayout,
                    })}
                >
                    {selectOptions.map((opt, optionIdx) => (
                        <div
                            key={opt.value}
                            className="flex items-center cursor-pointer"
                            role="button"
                            onClick={(e) => {
                                // Stop propagation + prevent default
                                // item's label would trigger a click on adjacent items
                                // Underlying cause unclear.
                                e.stopPropagation();
                                e.preventDefault();

                                // Get the updated checked state
                                const checked = !value.includes(opt.value);

                                const updatedValue = [
                                    ...value.filter((v) => v !== opt.value),
                                ];

                                if (checked) {
                                    Analytics.trackInlineMultiSelectValueSelected(
                                        {
                                            field: filterMetadata.field,
                                            value: opt.value,
                                        },
                                    );

                                    updatedValue.push(opt.value);
                                } else {
                                    Analytics.trackInlineMultiSelectValueDeselected(
                                        {
                                            field: filterMetadata.field,
                                            value: opt.value,
                                        },
                                    );
                                }

                                onChange(updatedValue);
                            }}
                        >
                            <input
                                id={`filter-${opt.value}`}
                                name={`option-${opt.value}`}
                                type="checkbox"
                                checked={value.includes(opt.value)}
                                readOnly
                                className="h-4 w-4 rounded-sm text-link focus:ring-blue-500"
                            />
                            <label
                                htmlFor={`filter-${optionIdx}`}
                                className="ml-3 pr-6 text-sm text-slate-800 flex items-center gap-2 truncate"
                            >
                                {showOptionIcons && (
                                    <ImageLoader
                                        src={opt.content.icon || RWA_COIN_ICON}
                                        alt={`${opt.content.label} Checkbox Icon`}
                                        className={cn(
                                            "h-5 w-5 shrink-0 rounded-full",
                                            {
                                                "opacity-50": !opt.content.icon,
                                            },
                                        )}
                                        fallbackSrc={RWA_COIN_ICON}
                                    />
                                )}
                                <span className="truncate">
                                    {opt.content.label}
                                </span>
                            </label>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
